import { apiClient } from './api-client';
import { ProductResponse } from '../dto/inventory/ProductResponse';
import { ProductGroup } from '../dto/inventory/ProductGroup';
import { ProductPurchase } from '../dto/inventory/ProductPurchase';
import { ErrorResponse } from '../dto/common/ErrorResponse';

/**
 * Inventory service.
 * Handles product/inventory operations.
 */
class InventoryService {
  /**
   * Gets active product categories.
   * @returns {Promise<{success: boolean, data?: Array<{id: string, code: string, name: string, subcategoryName: string|null, subcategoryCode: string|null, vatRate: number}>, error?: ErrorResponse}>}
   */
  async getCategories() {
    try {
      const response = await apiClient.get('/inventory/categories');

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const categories = response.data.map((item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        subcategoryName: item.subcategoryName ?? null,
        subcategoryCode: item.subcategoryCode ?? null,
        vatRate: item.vatRate,
      }));

      return {
        success: true,
        data: categories,
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Gets a single active category by code.
   * @param {string} code - Category code
   * @returns {Promise<{success: boolean, data?: {id: string, code: string, name: string, subcategoryName: string|null, subcategoryCode: string|null, vatRate: number}, error?: ErrorResponse}>}
   */
  async getCategoryByCode(code) {
    try {
      const response = await apiClient.get(`/inventory/categories/${code}`);

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: {
          id: response.data.id,
          code: response.data.code,
          name: response.data.name,
          subcategoryName: response.data.subcategoryName ?? null,
          subcategoryCode: response.data.subcategoryCode ?? null,
          vatRate: response.data.vatRate,
        }
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Gets a page of products in inventory, optionally filtered.
   * @param {string|null} search — Search query for product name
   * @param {string|null} barCode — Search query for product barcode
   * @param {string|null} category — Product category code or null for all
   * @param {string|null} brandId — Brand UUID or null for all
   * @param {boolean} includeInactive — Include inactive products (default: false)
   * @param {number} page — Zero-based page index (default: 0)
   * @param {number} pageSize — Number of items per page (default: 25)
   * @param {string} sortBy — Sort field (default: name)
   * @param {string} sortDir — Sort direction asc|desc (default: asc)
   * @returns {Promise<{success: boolean, data?: ProductResponse[], pagination?: object, error?: ErrorResponse}>}
   */
  async getInventory(
    search = null,
    barCode = null,
    category = null,
    brandId = null,
    includeInactive = false,
    page = 0,
    pageSize = 25,
    sortBy = 'name',
    sortDir = 'asc'
  ) {
    try {
      const params = {};
      if (search && search.trim()) params.search = search.trim();
      if (barCode && barCode.trim()) params.barCode = barCode.trim();
      if (category) params.category = category;
      if (brandId) params.brandId = brandId;
      if (includeInactive) params.includeInactive = true;
      params.page = page;
      params.pageSize = pageSize;
      params.sortBy = sortBy;
      params.sortDir = sortDir;
      
      const response = await apiClient.get('/inventory', { params });

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const { content, page: responsePage, pageSize: responsePageSize, totalElements, totalPages } = response.data;

      return {
        success: true,
        data: (content || []).map(item => ProductResponse.fromJSON(item)),
        pagination: {
          page: responsePage,
          pageSize: responsePageSize,
          totalElements,
          totalPages,
        },
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Gets products that should appear in inventory alerts.
   * Alerts include out-of-stock and low-stock active products.
   * @returns {Promise<{success: boolean, data?: ProductResponse[], error?: ErrorResponse}>}
   */
  async getInventoryAlerts() {
    try {
      const response = await apiClient.get('/inventory/alerts');

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: response.data.map(item => ProductResponse.fromJSON(item))
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Download product inventory as CSV.
   * @param {boolean} includeInactive - Include inactive products in export.
   * @returns {Promise<{success: boolean, data?: Blob, error?: ErrorResponse}>}
   */
  async exportProductsCsv(includeInactive = false) {
    try {
      const response = await apiClient.get('/inventory/products/export', {
        params: { includeInactive },
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error),
      };
    }
  }

  /**
   * Import products from CSV.
   * @param {File} file - CSV file.
   * @returns {Promise<{success: boolean, data?: object, error?: ErrorResponse}>}
   */
  async importProductsCsv(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/inventory/products/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error),
      };
    }
  }

  /**
   * Gets details of a specific product by ID.
   * @param {number} id — Product ID
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async getProductDetails(id) {
    try {
      const response = await apiClient.get(`/inventory/${id}`);

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const product = ProductResponse.fromJSON(response.data);

      return {
        success: true,
        data: product
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Creates a new product.
   * @param {CreateProductRequest} request — Product creation data
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async createProduct(request) {
    try {
      const validation = request.validate();
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await apiClient.post('/inventory', request.toJSON());

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const product = ProductResponse.fromJSON(response.data);

      return {
        success: true,
        data: product
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Duplicates an existing product as a new product.
   * @param {string} sourceProductId — Product ID to duplicate from
   * @param {Object} request — Duplicate product payload
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async duplicateProduct(sourceProductId, request) {
    try {
      if (request?.validate) {
        const validation = request.validate();
        if (!validation.valid) {
          throw new Error(validation.errors.join(', '));
        }
      }

      const payload = request?.toJSON ? request.toJSON() : request;
      const duplicatePayload = {
        name: payload.name,
        description: payload.description,
        sku: payload.sku,
        brandId: payload.brandId,
        categoryCode: payload.categoryCode,
        categoryName: payload.categoryName,
        listPrice: payload.listPrice,
        costPrice: payload.costPrice,
        stockQuantity: payload.stockQuantity,
        minStockLevel: payload.minStockLevel,
        weightGrams: payload.weightGrams,
        category: payload.category,
        daycareTime: payload.daycareTime,
        daycareBundle: payload.daycareBundle,
      };

      const response = await apiClient.post(`/inventory/${sourceProductId}/duplicate`, duplicatePayload);

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const product = ProductResponse.fromJSON(response.data);
      return {
        success: true,
        data: product,
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Updates an existing product.
   * @param {number} id — Product ID to update
   * @param {UpdateProductRequest} request — Product update data
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async updateProduct(id, request) {
    try {
      const validation = request.validate();
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await apiClient.put(`/inventory/${id}`, request.toJSON());

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const product = ProductResponse.fromJSON(response.data);

      return {
        success: true,
        data: product
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }


  /**
   * Marks one gallery image as the product's main image.
   * @param {string} productId — Product ID
   * @param {string} imageId — Gallery image ID
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async setPrimaryProductImage(productId, imageId) {
    try {
      const response = await apiClient.put(`/inventory/${productId}/images/${imageId}/primary`);

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: ProductResponse.fromJSON(response.data)
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Removes a single image from a product's gallery.
   *
   * The API deactivates the image rather than deleting it, so this is reversible
   * server-side; the gallery simply no longer lists it.
   * @param {string} productId — Product ID
   * @param {string} imageId — Gallery image ID
   * @returns {Promise<{success: boolean, error?: ErrorResponse}>}
   */
  async removeProductImage(productId, imageId) {
    try {
      await apiClient.delete(`/inventory/${productId}/images/${imageId}`);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Gets the purchase history of a product (newest first).
   * @returns {Promise<{success: boolean, data?: {content: ProductPurchase[], totalElements: number, totalPages: number, page: number}, error?: ErrorResponse}>}
   */
  async getProductPurchases(productId, page = 0, pageSize = 25) {
    try {
      const response = await apiClient.get(`/inventory/${productId}/purchases`, {
        params: { page, pageSize }
      });
      const body = response.data || {};
      return {
        success: true,
        data: {
          ...body,
          content: (body.content || []).map((p) => ProductPurchase.fromJSON(p))
        }
      };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Adds a purchase to a product.
   * @param {string} productId
   * @param {CreateProductPurchaseRequest} request
   */
  async addProductPurchase(productId, request) {
    try {
      const response = await apiClient.post(`/inventory/${productId}/purchases`, request.toJSON());
      return { success: true, data: ProductPurchase.fromJSON(response.data) };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async removeProductPurchase(productId, purchaseId) {
    try {
      await apiClient.delete(`/inventory/${productId}/purchases/${purchaseId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Applies a signed stock adjustment with a mandatory reason.
   * @param {string} productId
   * @param {{delta: number, reason: string}} payload
   */
  async adjustStock(productId, payload) {
    try {
      const response = await apiClient.post(`/inventory/${productId}/stock/adjustments`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /** Stock movement history of a product (newest first). */
  async getStockMovements(productId, page = 0, pageSize = 25) {
    try {
      const response = await apiClient.get(`/inventory/${productId}/stock/movements`, {
        params: { page, pageSize }
      });
      return { success: true, data: response.data ?? { content: [], totalElements: 0 } };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Archives (deactivates) or reactivates a product.
   * @param {string} id
   * @param {{archived: boolean, reason: string}} payload
   */
  async archiveProduct(id, payload) {
    try {
      const response = await apiClient.patch(`/inventory/${id}/archive`, payload);
      if (!response.data) {
        throw new Error('Empty response from server');
      }
      return { success: true, data: ProductResponse.fromJSON(response.data) };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Per-product e-commerce switch.
   * @param {string} id
   * @param {boolean|null} enabled — true on, false off, null = automatic default
   */
  async setCommerceEnabled(id, enabled) {
    try {
      const response = await apiClient.patch(`/inventory/${id}/commerce`, { enabled });
      if (!response.data) {
        throw new Error('Empty response from server');
      }
      return { success: true, data: ProductResponse.fromJSON(response.data) };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }


  /**
   * Product tags (customer-facing short labels shown over the store image).
   */
  async getTags(productId) {
    try {
      const response = await apiClient.get(`/inventory/${productId}/tags`);
      return { success: true, data: response.data ?? [] };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async addTag(productId, text) {
    try {
      const response = await apiClient.post(`/inventory/${productId}/tags`, { text });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async updateTag(productId, tagId, text) {
    try {
      const response = await apiClient.put(`/inventory/${productId}/tags/${tagId}`, { text });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async removeTag(productId, tagId) {
    try {
      await apiClient.delete(`/inventory/${productId}/tags/${tagId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Product notes (internal staff notes, ERP-only).
   */
  async getNotes(productId) {
    try {
      const response = await apiClient.get(`/inventory/${productId}/notes`);
      return { success: true, data: response.data ?? [] };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async addNote(productId, text) {
    try {
      const response = await apiClient.post(`/inventory/${productId}/notes`, { text });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async updateNote(productId, noteId, text) {
    try {
      const response = await apiClient.put(`/inventory/${productId}/notes/${noteId}`, { text });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  async removeNote(productId, noteId) {
    try {
      await apiClient.delete(`/inventory/${productId}/notes/${noteId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorResponse.fromError(error) };
    }
  }

  /**
   * Deletes a product (soft delete - marks as inactive).
   * @param {number} id — Product ID to delete
   * @returns {Promise<{success: boolean, error?: ErrorResponse}>}
   */
  async deleteProduct(id) {
    try {
      await apiClient.delete(`/inventory/${id}`);

      return {
        success: true
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Publishes or unpublishes a product on the public storefront.
   *
   * <p>Storefront visibility is independent of `isActive`; publishing requires the product
   * to be active and generates a slug on demand if one is missing.
   *
   * @param {string} id — Product ID
   * @param {boolean} publiclyVisible — true to publish, false to hide
   * @returns {Promise<{success: boolean, data?: ProductResponse, error?: ErrorResponse}>}
   */
  async setPublicVisibility(id, publiclyVisible) {
    try {
      const response = await apiClient.patch(`/inventory/${id}/public-visibility`, null, {
        params: { publiclyVisible }
      });

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: ProductResponse.fromJSON(response.data)
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Lists product variation groups for the tenant.
   * @returns {Promise<{success: boolean, data?: ProductGroup[], error?: ErrorResponse}>}
   */
  async getProductGroups() {
    try {
      const response = await apiClient.get('/inventory/groups');

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: response.data.map((item) => ProductGroup.fromJSON(item)),
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Gets a single product variation group.
   * @param {string} groupId — Group ID
   * @returns {Promise<{success: boolean, data?: ProductGroup, error?: ErrorResponse}>}
   */
  async getProductGroup(groupId) {
    try {
      const response = await apiClient.get(`/inventory/groups/${groupId}`);

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: ProductGroup.fromJSON(response.data),
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Creates a product variation group.
   * @param {ProductGroupRequest} request — Group definition
   * @returns {Promise<{success: boolean, data?: ProductGroup, error?: ErrorResponse}>}
   */
  async createProductGroup(request) {
    try {
      const validation = request.validate();
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await apiClient.post('/inventory/groups', request.toJSON());

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: ProductGroup.fromJSON(response.data),
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Updates a product variation group.
   *
   * Attributes and members are authoritative: omitted entries are deactivated server-side.
   * @param {string} groupId — Group ID
   * @param {ProductGroupRequest} request — Group definition
   * @returns {Promise<{success: boolean, data?: ProductGroup, error?: ErrorResponse}>}
   */
  async updateProductGroup(groupId, request) {
    try {
      const validation = request.validate();
      if (!validation.valid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await apiClient.put(`/inventory/groups/${groupId}`, request.toJSON());

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      return {
        success: true,
        data: ProductGroup.fromJSON(response.data),
      };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Deactivates a product variation group.
   *
   * The member products are untouched and immediately behave as ungrouped products.
   * @param {string} groupId — Group ID
   * @returns {Promise<{success: boolean, error?: ErrorResponse}>}
   */
  async deleteProductGroup(groupId) {
    try {
      await apiClient.delete(`/inventory/groups/${groupId}`);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }
}

export default new InventoryService();
